import React, {useEffect, useRef, useState} from "react";
import '../../styles/components/lesson/control-bar.scss';
import ControlBarButton from "./ControlBarButton";
import SegmentSelector from "./SegmentSelector";
import {TempoSelector} from "./TempoSelector";
import DemoModeSelector from "./DemoModeSelector";
import SettingsSelector from "./SettingsSelector";
import {subscribeToMidiEvents} from "../../services/eventBus/midi/midiEventBus";
import {MidiEventTypes} from "../../services/eventBus/midi/MidiEventTypes";
import settingsService from "../../services/settings/settingsService";
import {getAudioContext} from "../../services/audioContext";
import {FontIcon} from "../common/FontIcon";
import deviceInfo from "../../services/deviceInfo/deviceInfo";
import {useNavigate} from "react-router-dom";
import {setProgressBarVisibility} from "./progressBar/ProgressBarLogic";
import {useAppSelector} from "../../redux/hooks";
import {lessonPlayerStore} from "../../redux/lesson/LessonPlayerStore";
import playbackOrchestrator from "../../services/playbackOrchestrator/playbackOrchestrator";
import PitchDetectionIndicator from "./pitchDetectionIndicator/PitchDetectionIndicator";
import {controlBarClicked, goToNextSegment, goToPreviousSegment} from "../../services/lesson/lessonUtility";
import {appPagePaths} from "../../config/config";

interface IControlBarProps {
  onPlayStopPauseButton_clicked: () => void;
  canMicrophoneAccessModalBeShown: () => boolean;
}

function getKeyboardHeight() {
  const keyboardEl = document.querySelector(`.keyboard`);
  if (!keyboardEl){ return 0;}
  //@ts-ignore
  return keyboardEl.offsetHeight;
}

const hideControlBarAfterNms = 4000;


/**
 * Control bar hiding notes:
 * - when the settings menu is open, and play is pressed, the setting menu closes (it closes anytime it loses focus),
 * and the control bar is hidden.
 */
export default function ControlBar({onPlayStopPauseButton_clicked, canMicrophoneAccessModalBeShown}: IControlBarProps) {
  const controlBarTogglesEnabledState = useAppSelector(lessonPlayerStore.getControlBarTogglesEnabledState);
  const lesson = useAppSelector(lessonPlayerStore.getLesson);
  const segment = useAppSelector(lessonPlayerStore.getSegment);
  const indexOfCurrentSegment = lesson?.segments.findIndex(s => segment?.id === s.id && s.mode === segment?.mode) ?? 0;
  const numberOfSegments = lesson?.segments.length ?? 0;
  const hasVideo = segment?.hasVideo ?? false;
  const isNextSegmentButtonEnabled = indexOfCurrentSegment + 1 < numberOfSegments;
  const isPreviousSegmentButtonEnabled = indexOfCurrentSegment > 0;
  const isControlBarPartiallyHidden = useAppSelector(lessonPlayerStore.getIsControlBarPartiallyHidden);
  const isSettingsDropdownSelectorOpen = useAppSelector(lessonPlayerStore.getIsSettingsSelectorDropdownOpen);
  const isSegmentDropdownSelectorOpen = useAppSelector(lessonPlayerStore.getIsSegmentSelectorDropdownOpen);
  const isDemoModeDropdownSelectorOpen = useAppSelector(lessonPlayerStore.getIsDemoSelectorDropdownOpen);
  const isTempoDropdownSelectorOpen = useAppSelector(lessonPlayerStore.getIsTempoSelectorDropdownOpen);
  const isPlaying = useAppSelector(lessonPlayerStore.getIsPlaying);
  const isPlaybackEnabled = useAppSelector(lessonPlayerStore.getIsPlaybackEnabled);

  const isLoopEnabled = useAppSelector(lessonPlayerStore.getLoopEnabled);
  const isCountInPlaying = useAppSelector(lessonPlayerStore.getIsCountInPlaying);
  const areAnyControlBarMenusOpen = isSettingsDropdownSelectorOpen || isSegmentDropdownSelectorOpen || isDemoModeDropdownSelectorOpen || isTempoDropdownSelectorOpen;

  const hasPlayingStarted = isPlaying || isCountInPlaying;
  const shouldControlBarBeHidden = deviceInfo.getIsMobile() && hasPlayingStarted && !areAnyControlBarMenusOpen;
  //in order to access this inside of a setTimeout, we need a ref https://github.com/facebook/react/issues/14010
  const shouldControlBarBeHiddenRef = useRef(shouldControlBarBeHidden);
  shouldControlBarBeHiddenRef.current = shouldControlBarBeHidden;

  const controlBarIsPlayingClass = isControlBarPartiallyHidden ? "is-playing" : "is-not-playing";

  const playOrStopIcon = hasPlayingStarted ? "icon-stop" : "icon-play";
  const playOrPauseIcon = hasPlayingStarted ? "icon-pause" : "icon-play";
  const playPauseAccessibleLabel = setPlayPauseAccessibleLabel(isPlaying, hasVideo);
  const iconClass = hasVideo ? playOrPauseIcon : playOrStopIcon;
  const playOrStopOrPauseIcon = <FontIcon icon={iconClass}/>

  const playButtonEl = isPlaybackEnabled 
    ? <ControlBarButton 
      tooltip={playPauseAccessibleLabel} 
      ariaLabel={playPauseAccessibleLabel} 
      onTouchStart={()=> getAudioContext().resume()} 
      onMouseDown={() => getAudioContext().resume()}
      onClick={(ev: React.MouseEvent)=> { ev.stopPropagation(); onPlayStopPauseButton_clicked(); }}
      toggledOn={isPlaying}
      className="play-button">{playOrStopOrPauseIcon}</ControlBarButton> 
    : createPlayButtonLoadingSpinner();
  const midiKeysCurrentlyPressed = new Set<number>();
  let isFootPedalPressed = false;

  const navigate = useNavigate();

  //hide the control bar when on mobile so there is more room on the screen.
  const [controlBarVisibilityClass, setControlBarVisibilityClass] = useState(``);

  //when settings menu is closed, when count in starts.
  useEffect(() => {
    setControlBarVisibilityClass( shouldControlBarBeHiddenRef.current ? `hidden` : ``);
  }, [shouldControlBarBeHidden]);

  useEffect(() => {
    setProgressBarVisibility();
  }, [isPlaying]);

  useEffect(() => subscribeToMidiEvents((message: MidiEventTypes) => {
    switch (message.type) {
      case "midiFootPedalPressed":
        isFootPedalPressed = true;
        break;
      case "midiFootPedalReleased":
        isFootPedalPressed = false;
        // fall-through
      case "midiNotePlayed":
        midiKeysCurrentlyPressed.add(message.number);
        const isLowestNoteOnUsersKeyboardBeingPressed = midiKeysCurrentlyPressed.has(settingsService.getKeyboardKeysSettings().lowestNote);
        const isHighestNoteOnUsersKeyboardBeingPressed = midiKeysCurrentlyPressed.has(settingsService.getKeyboardKeysSettings().highestNote);
        const isNotPlayingAndEndNotesPressedAtSameTime =
          !isPlaying
          && !isCountInPlaying
          && isLowestNoteOnUsersKeyboardBeingPressed
          && isHighestNoteOnUsersKeyboardBeingPressed;
        if(isNotPlayingAndEndNotesPressedAtSameTime ){
          onPlayStopPauseButton_clicked();
        }
        if((isPlaying || isCountInPlaying) && isLowestNoteOnUsersKeyboardBeingPressed && isHighestNoteOnUsersKeyboardBeingPressed){
          onPlayStopPauseButton_clicked();
        }
        break;
      case "midiNoteStopped":
        midiKeysCurrentlyPressed.delete(message.number);
        break;
    }
  }, `ControlBar`), [isPlaying, isCountInPlaying]);

  let timeoutId2 = 0;
  function handleControlBarOverlayClick() {
    window.clearTimeout(timeoutId2);//stop existing timeouts from firing.
    setControlBarVisibilityClass(``); //when touched, do not hide, no matter what's going on
    timeoutId2 = window.setTimeout(()=>{
      setControlBarVisibilityClass( shouldControlBarBeHiddenRef.current ? `hidden` : ``);
    }, hideControlBarAfterNms);
  }

  // This logic is also used in the BackButton component.  Search the codebase for c0928j09 to see it.
  // An explanation of what's going on is available there.
  const goBack = () => {
    lessonPlayerStore.setActiveLessonId(undefined);
    lessonPlayerStore.setSpacingForActiveLesson(undefined);

    if (history.length <= 1) {
      navigate(appPagePaths.dashboardPage);
      return;
    }

    navigate(-1);
  }

  return (
    <>
      {controlBarVisibilityClass === `hidden`
        ? <div
          style={{height: `calc(100vh - ${getKeyboardHeight()}px`}}
          className={`control-bar-overlay-click-intercept`}
          onClick={() => handleControlBarOverlayClick()}
        >&nbsp;</div>
        : null}
      <div className={`control-bar ${controlBarIsPlayingClass} ${controlBarVisibilityClass}`} onClick={controlBarClicked}>
        {/* Eventually this button could be replaced with the BackButton component, I think. */}
        <div className={`back-and-segments`}>
          <button
            className="back-button2"
            title={"Go back"}
            aria-label={"Go back"}
            onClick={() => {
              playbackOrchestrator.stop();
              goBack();
            }}>
            <FontIcon icon={"icon-arrow-left"}/>
          </button>
          <SegmentSelector/>
        </div>
        <div className={`playback-controls`}>
          <ControlBarButton
            onClick={() => { goToPreviousSegment(navigate); }}
            tooltip={"Previous"}
            ariaLabel={"Previous segment"}
            className="previous-segment-button"
            disabled={!isPreviousSegmentButtonEnabled}>
            <FontIcon icon={"icon-angle-left"}/>
          </ControlBarButton>
          {playButtonEl}
          <ControlBarButton
            onClick={() => { goToNextSegment(navigate); }}
            tooltip={"Next"}
            ariaLabel={"Next segment"}
            className="next-segment-button"
            disabled={!isNextSegmentButtonEnabled}>
            <FontIcon icon={"icon-angle-right"}/>
          </ControlBarButton>
        </div>
        <div className={`player-feature-controls`}>
          <PitchDetectionIndicator canMicrophoneAccessModalBeShown={canMicrophoneAccessModalBeShown}/>
          <DemoModeSelector/>
          <ControlBarButton
            onClick={() => playbackOrchestrator.toggleIsLoopEnabled()}
            tooltip={"Loop"}
            ariaLabel={"Loop"}
            toggledOn={isLoopEnabled}
            className="loop-button"
            disabled={!controlBarTogglesEnabledState.isLoopButtonEnabled}>
            <FontIcon icon={"icon-repeat"}/>
          </ControlBarButton>
          <TempoSelector/>
          <SettingsSelector/>
        </div>
      </div>
    </>
  );
}

function createPlayButtonLoadingSpinner(){
  return <div className="play-button-loading">
    <div className="sk-fading-circle">
      <div className="sk-circle1 sk-circle"></div>
      <div className="sk-circle2 sk-circle"></div>
      <div className="sk-circle3 sk-circle"></div>
      <div className="sk-circle4 sk-circle"></div>
      <div className="sk-circle5 sk-circle"></div>
      <div className="sk-circle6 sk-circle"></div>
      <div className="sk-circle7 sk-circle"></div>
      <div className="sk-circle8 sk-circle"></div>
      <div className="sk-circle9 sk-circle"></div>
      <div className="sk-circle10 sk-circle"></div>
      <div className="sk-circle11 sk-circle"></div>
      <div className="sk-circle12 sk-circle"></div>
    </div>
  </div>;
}

function setPlayPauseAccessibleLabel(isPlaying: boolean, hasVideo: boolean): string {
  if (isPlaying && hasVideo) {
    return 'Pause'
  }

  if (isPlaying) {
    return 'Stop'
  }

  return 'Play'
}
